import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CardContainer from "../../../../components/Card/CardContainer";
import CardMedia from "../../../../components/Media/CardMedia";
import ModalMedia from "../../../../components/Modal/ModalMedia";
import UploadFileButton from "../../../../components/Buttons/UploadFileButton";
import SectionHeader from "./SectionHeader";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  SECTION_PRODUCT_IMAGES_TITLE,
  SECTION_PRODUCT_IMAGES_SUBTITLE,
  BUTTON_UPLOAD_PRODUCT_IMAGES,
} from "../../../../i18n/keysTranslations";
import {
  LOGO_IMAGE_FORMATS,
  FONT_SIZE_BUTTON,
  BORDER_RADIUS,
} from "../../../../utils/constants";
import {
  BORDER_GRAY_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";

const PRODUCT_IMAGE_CARD_WIDTH = 160;

const subtleHorizontalScrollbarSx = {
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  pb: 1,
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0, 0, 0, 0.18) transparent",
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.18)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.28)",
  },
};

const ProductImagePlaceholder = ({ onUploadClick }) => {
  return (
    <Box
      onClick={onUploadClick}
      sx={{
        height: 160,
        width: 160,
        maxWidth: "100%",
        borderRadius: 2,
        border: `2px dashed ${INPUT_COLOR_BORDER}`,
        backgroundColor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "text.primary",
          backgroundColor: "action.selected",
        },
      }}
    >
      <AddIcon
        sx={{
          fontSize: 40,
          color: "text.secondary",
        }}
      />
    </Box>
  );
};

const ProductImageCard = ({ productImage, onOpenModal, onDelete }) => {
  const { imageUrl } = productImage;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        width: PRODUCT_IMAGE_CARD_WIDTH,
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${BORDER_GRAY_COLOR}`,
        borderColor: INPUT_COLOR_BORDER,
        bgcolor: "background.paper",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <CardMedia
        url={imageUrl}
        onClick={() => onOpenModal(imageUrl)}
        sx={{
          cursor: "pointer",
          objectFit: "cover",
          objectPosition: "center",
          height: 160,
          width: "100%",
          backgroundColor: "action.hover",
          "&:hover": {
            filter: "brightness(0.85)",
          },
        }}
      />
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
        aria-label="Delete product image"
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          bgcolor: "background.paper",
          color: "text.secondary",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

export const ProductImagesSection = ({ formMethods, hasError = false }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  if (!formMethods || !formMethods.control) {
    return null;
  }

  const {
    getValues,
    errors,
    appendBusinessProductImages = () => {},
    removeBusinessProductImage = () => {},
  } = formMethods;
  const businessProductImages =
    formMethods?.watch?.("businessProductImages") || [];

  const onOpenModal = (url) => {
    if (!url) return;
    setModalImageUrl(url);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setModalImageUrl(null);
  };

  const onPlaceholderClick = () => {
    const input = document.getElementById("upload-product-images");
    if (input) {
      input.click();
    }
  };

  const onImageUpload = (newUrls = []) => {
    newUrls.forEach((url) => {
      if (!url?.trim()) return;
      appendBusinessProductImages({ imageUrl: url });
    });
  };

  const onDeleteImage = ({ imageUrl }) => {
    const currentImages = getValues("businessProductImages") || [];
    const imageIndex = currentImages.findIndex(
      (item) => item.imageUrl === imageUrl
    );

    if (imageIndex !== -1) {
      removeBusinessProductImage(imageIndex);
    }
  };

  const onDeleteFromModal = () => {
    if (modalImageUrl) {
      onDeleteImage({ imageUrl: modalImageUrl });
    }
    onCloseModal();
  };

  const productImages = Array.isArray(businessProductImages)
    ? businessProductImages
    : [];

  return (
    <>
      <CardContainer title="" hasError={hasError}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            icon={Inventory2Icon}
            title={SECTION_PRODUCT_IMAGES_TITLE}
            subtitle={SECTION_PRODUCT_IMAGES_SUBTITLE}
          />

          {errors["businessProductImages"] && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontSize: "0.75rem" }}
            >
              {errors["businessProductImages"].message}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {productImages.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "nowrap",
                  ...subtleHorizontalScrollbarSx,
                }}
              >
                {productImages.map((productImage) => (
                  <ProductImageCard
                    key={productImage.imageUrl}
                    productImage={productImage}
                    onOpenModal={onOpenModal}
                    onDelete={() =>
                      onDeleteImage({ imageUrl: productImage.imageUrl })
                    }
                  />
                ))}
              </Box>
            ) : (
              <ProductImagePlaceholder onUploadClick={onPlaceholderClick} />
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <UploadFileButton
                id="upload-product-images"
                textUpload={BUTTON_UPLOAD_PRODUCT_IMAGES}
                colorUpload="secondary"
                disableVerificationMedia={true}
                onChange={onImageUpload}
                pathStorage={`socialMedia/${userID}/products/`}
                acceptedFormats={LOGO_IMAGE_FORMATS}
                modeUpload="sync"
                multiple={true}
                loading={isUploading}
                onCallbackLoading={(loading) => setIsUploading(loading)}
                sx={{
                  fontSize: FONT_SIZE_BUTTON,
                }}
              />
            </Box>
          </Box>
        </Box>
      </CardContainer>

      {isModalOpen && (
        <ModalMedia
          modalOpen={isModalOpen}
          onCloseModal={onCloseModal}
          url={modalImageUrl}
          showEditButton={false}
          showShareButton={false}
          showDownloadButton={true}
          showDeleteButton={true}
          onDelete={onDeleteFromModal}
        />
      )}
    </>
  );
};

export default ProductImagesSection;
